import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
  PieChart, Pie, Cell, BarChart, Bar,
} from 'recharts';
import './App.css';

const API = '';
const COLORS = ['#6366f1','#f43f5e','#f59e0b','#10b981','#3b82f6','#8b5cf6','#ec4899','#14b8a6','#f97316','#06b6d4'];

function formatDuration(seconds) {
  if (!seconds) return 'N/A';
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  if (d > 0) return `${d}d ${h}h ${m}m ${s}s`;
  if (h > 0) return `${h}h ${m}m ${s}s`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

function App() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [site, setSite] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = { days };
      if (site) params.site = site;
      const res = await axios.get(`${API}/api/summary`, { params });
      setData(res.data);
    } catch (err) {
      setError('Failed to load analytics data. Is the server running?');
    }
    setLoading(false);
  }, [days, site]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) return (
    <div className="loading-screen">
      <div className="spinner"></div>
      <p>Loading analytics...</p>
    </div>
  );
  if (error) return <div className="loading-screen"><p>{error}</p></div>;
  if (!data) return null;

  return (
    <div className="app">
      {/* Header */}
      <div className="header">
        <div className="header-left">
          <div className="header-icon">&#x1f4ca;</div>
          <div>
            <h1 className="header-title">Analytics Dashboard</h1>
            <p className="header-subtitle">Spatial Browser Visitor Metrics</p>
          </div>
        </div>
        <div className="controls">
          {data.sites.length > 0 && (
            <select value={site} onChange={e => setSite(e.target.value)} className="select">
              <option value="">All Sites</option>
              {data.sites.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          )}
          <select value={days} onChange={e => setDays(Number(e.target.value))} className="select">
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
            <option value={365}>Last year</option>
            <option value={9999}>All time</option>
          </select>
          <button onClick={fetchData} className="refresh-btn">Refresh</button>
        </div>
      </div>

      <div className="container">
        {/* Summary Cards */}
        <div className="cards">
          <div className="card card-views">
            <div className="card-icon-svg">
              <svg viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.7)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
            </div>
            <div className="card-content">
              <div className="card-label">Total Page Views</div>
              <div className="card-value">{data.total_visits.toLocaleString()}</div>
            </div>
          </div>
          <div className="card card-visitors">
            <div className="card-icon-svg">
              <svg viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.7)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
            </div>
            <div className="card-content">
              <div className="card-label">Unique Visitors</div>
              <div className="card-value">{data.unique_visitors.toLocaleString()}</div>
            </div>
          </div>
          <div className="card card-time">
            <div className="card-icon-svg">
              <svg viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.7)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
            </div>
            <div className="card-content">
              <div className="card-label">Avg. Time on Page</div>
              <div className="card-value">{formatDuration(data.avg_time_on_page)}</div>
            </div>
          </div>
          <div className="card card-total-time">
            <div className="card-icon-svg">
              <svg viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.7)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 22h14"/><path d="M5 2h14"/><path d="M17 22v-4.172a2 2 0 0 0-.586-1.414L12 12l-4.414 4.414A2 2 0 0 0 7 17.828V22"/><path d="M7 2v4.172a2 2 0 0 0 .586 1.414L12 12l4.414-4.414A2 2 0 0 0 17 6.172V2"/></svg>
            </div>
            <div className="card-content">
              <div className="card-label">Total Time on Page</div>
              <div className="card-value">{formatDuration(data.total_time_on_page)}</div>
            </div>
          </div>
        </div>

        {/* Cumulative Visits Over Time */}
        <div className="chart-box">
          <h3 className="chart-title">Cumulative Visits Over Time</h3>
          <ResponsiveContainer width="100%" height={320}>
            <AreaChart data={data.cumulative_daily}>
              <defs>
                <linearGradient id="colorViews" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#6366f1" stopOpacity={0}/>
                </linearGradient>
                <linearGradient id="colorUnique" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="date" tick={{ fontSize: 12, fill: '#888' }} axisLine={{ stroke: '#e0e0e0' }} />
              <YAxis yAxisId="left" tick={{ fontSize: 12, fill: '#6366f1' }} axisLine={{ stroke: '#e0e0e0' }} />
              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 12, fill: '#10b981' }} axisLine={{ stroke: '#e0e0e0' }} />
              <Tooltip contentStyle={{ borderRadius: 8, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
              <Legend />
              <Area yAxisId="left" type="monotone" dataKey="total_views" stroke="#6366f1" strokeWidth={2.5} fill="url(#colorViews)" name="Total Views" />
              <Area yAxisId="right" type="monotone" dataKey="total_unique" stroke="#10b981" strokeWidth={2.5} fill="url(#colorUnique)" name="Unique Visitors" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Countries + Browsers */}
        <div className="two-col">
          <div className="chart-box">
            <h3 className="chart-title">Top Countries</h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie data={data.countries} dataKey="count" nameKey="country" cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={3}
                  label={({ country, percent }) => `${country} ${(percent * 100).toFixed(0)}%`}>
                  {data.countries.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={{ borderRadius: 8, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="chart-box">
            <h3 className="chart-title">Browsers</h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie data={data.browsers} dataKey="count" nameKey="browser" cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={3}
                  label={({ browser, percent }) => `${browser} ${(percent * 100).toFixed(0)}%`}>
                  {data.browsers.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={{ borderRadius: 8, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Cities + OS */}
        <div className="two-col">
          <div className="chart-box">
            <h3 className="chart-title">Top Cities</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={data.cities} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 12, fill: '#888' }} />
                <YAxis dataKey="city" type="category" width={150} tick={{ fontSize: 11, fill: '#555' }} />
                <Tooltip contentStyle={{ borderRadius: 8, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
                <Bar dataKey="count" fill="#6366f1" radius={[0, 6, 6, 0]} barSize={20} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="chart-box">
            <h3 className="chart-title">Operating Systems</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={data.os} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 12, fill: '#888' }} />
                <YAxis dataKey="os" type="category" width={120} tick={{ fontSize: 11, fill: '#555' }} />
                <Tooltip contentStyle={{ borderRadius: 8, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
                <Bar dataKey="count" fill="#10b981" radius={[0, 6, 6, 0]} barSize={20} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Devices + Referrers */}
        <div className="two-col">
          <div className="chart-box">
            <h3 className="chart-title">Desktop vs Mobile</h3>
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={data.devices} dataKey="count" nameKey="device" cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={5}
                  label={({ device, percent }) => `${device} ${(percent * 100).toFixed(0)}%`}>
                  {data.devices.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={{ borderRadius: 8, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="chart-box">
            <h3 className="chart-title">Top Referrers</h3>
            {data.referrers.length > 0 ? (
              <table className="data-table">
                <thead>
                  <tr><th>Referrer</th><th>Visits</th></tr>
                </thead>
                <tbody>
                  {data.referrers.map((r, i) => (
                    <tr key={i}><td>{r.referrer}</td><td className="td-right"><span className="badge">{r.count}</span></td></tr>
                  ))}
                </tbody>
              </table>
            ) : <p className="empty-text">No referrer data yet</p>}
          </div>
        </div>

        {/* Recent Visits */}
        <div className="chart-box">
          <h3 className="chart-title">Recent Visits</h3>
          <div className="table-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Time</th>
                  <th>IP</th>
                  <th>Page</th>
                  <th>Location</th>
                  <th>Browser / OS</th>
                  <th>Duration</th>
                </tr>
              </thead>
              <tbody>
                {data.recent.map((v, i) => {
                  const t = new Date(v.timestamp);
                  const dur = v.time_on_page_seconds ? formatDuration(v.time_on_page_seconds) : '-';
                  return (
                    <tr key={i}>
                      <td>{t.toLocaleDateString()} {t.toLocaleTimeString()}</td>
                      <td><code className="ip-code">{v.ip}</code></td>
                      <td>{v.path}</td>
                      <td>{v.city}, {v.country}</td>
                      <td>{v.browser} / {v.os}</td>
                      <td>{dur}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;
